import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useLocalStorage } from '@/composables/useLocalStorage'
import { BASKET_KEY } from '@/constants/storage'
import { useMenuStore } from '@/stores/menu'
import { useUserStore } from '@/stores/user'
import type { CartModifier } from '@/utils/modifiers'
import { searchKey } from '@/utils/search'
import { FLOW, track } from '@/utils/track'

/** Позиция корзины (§6 LOGIC.md). Одинаковый товар с разными допами — разные строки. */
export interface CartItem {
  productId: string
  quantity: number
  /** Цена одной штуки вместе с допами — фиксируется в момент добавления */
  price: number
  modifiers: CartModifier[]
  /**
   * Ингредиенты, которые клиент попросил убрать: в заказ уходят отдельным
   * полем `excludedIngredients` — просто массив id, без количеств и цен.
   */
  excluded?: string[]
  /** Уникальный ключ строки — по нему корзина отличает позиции */
  key: number
}

/**
 * Пакет клиент не выбирает — он кладётся сам к первому же товару. В меню это
 * обычный товар со своей ценой, поэтому и в корзине он живёт обычной строкой:
 * так он попадает и в расчёт акций, и в заказ, без отдельного поля.
 *
 * Ищем по имени, а не по id: id у товара своё в каждой выгрузке CRM, а имя
 * одинаково на всех языках («Пакет», «Paket») — за это отвечает `searchKey`.
 */
const PACKAGE_NAME = searchKey('Пакет')

/** Больше пяти наборов приборов на один заказ не кладём. */
export const MAX_PERSONS = 5

export const useCartStore = defineStore('cart', () => {
  const menuStore = useMenuStore()
  const userStore = useUserStore()

  const basket = useLocalStorage<CartItem[]>(BASKET_KEY, [])

  /**
   * Сколько наборов приборов положить. Уходит в заказ полем `person`, поэтому
   * живёт в сторе, а не на экране корзины: собирает заказ уже оформление.
   */
  const persons = ref(1)

  /**
   * Счётчик приборов: ниже нуля и выше `MAX_PERSONS` не уходим. Лимит держим
   * здесь, а не на кнопке, чтобы он работал для любого, кто меняет счётчик.
   */
  function changePersons(step: number): void {
    persons.value = Math.min(MAX_PERSONS, Math.max(0, persons.value + step))
  }

  /*
   * Товары, которых нет в текущем меню (сменился филиал или регион), из
   * корзины пропадают, но в localStorage остаются: вернулся в свой филиал —
   * и позиции снова на месте. Поэтому правки ниже идут по basket, а не по cart.
   */
  const cart = computed(() =>
    basket.value.filter((item) => menuStore.productsEnum[item.productId]),
  )

  const packageProduct = computed(
    () => menuStore.products.find((item) => searchKey(item.name) === PACKAGE_NAME) ?? null,
  )

  /** Id пакета — по нему корзина рисует строку без степпера. */
  const packageId = computed(() => packageProduct.value?.id ?? '')

  /** Корзина без служебного пакета — по ней и решаем, нужен ли он вообще. */
  const goods = computed(() =>
    cart.value.filter((item) => item.productId !== packageProduct.value?.id),
  )

  /** Сколько штук товара уже в корзине — суммарно по всем наборам допов. */
  const cartProducts = computed<Record<string, number>>(() =>
    cart.value.reduce<Record<string, number>>((acc, item) => {
      acc[item.productId] = (acc[item.productId] ?? 0) + item.quantity
      return acc
    }, {}),
  )

  const totalSum = computed(() =>
    cart.value.reduce((sum, item) => sum + item.price * item.quantity, 0),
  )

  const totalCount = computed(() => cart.value.reduce((sum, item) => sum + item.quantity, 0))

  /**
   * Строка с тем же товаром, тем же набором допов и тем же составом — в неё
   * доливаем количество. Бургер с луком и без лука — разные строки.
   */
  function findRowKey(item: Omit<CartItem, 'key'>): number {
    const modifiers = JSON.stringify(item.modifiers)
    const excluded = JSON.stringify(item.excluded ?? [])

    const same = cart.value.find(
      (row) =>
        row.productId === item.productId &&
        JSON.stringify(row.modifiers) === modifiers &&
        JSON.stringify(row.excluded ?? []) === excluded,
    )
    return same?.key ?? 0
  }

  function addToCart(item: Omit<CartItem, 'key'>): void {
    const key = findRowKey(item)

    /* Название, а не только id: иначе отчёт читается как список гуидов */
    track(FLOW.cartAdded, {
      product: menuStore.productsEnum[item.productId]?.name ?? item.productId,
      quantity: item.quantity,
      price: item.price,
      modifiers: item.modifiers.length,
    })

    basket.value = key
      ? basket.value.map((row) =>
          row.key === key ? { ...row, quantity: row.quantity + item.quantity } : row,
        )
      : [...basket.value, { ...item, key: Date.now() }]
  }

  /**
   * Минус на карточке товара. У товара может быть несколько строк с разными
   * допами — снимаем штуку с последней добавленной, как в веб-версии.
   */
  function removeLastCartItem(productId: string): void {
    const last = [...cart.value].reverse().find((row) => row.productId === productId)
    if (!last) return

    basket.value =
      last.quantity > 1
        ? basket.value.map((row) =>
            row.key === last.key ? { ...row, quantity: row.quantity - 1 } : row,
          )
        : basket.value.filter((row) => row.key !== last.key)
  }

  /** Степпер в корзине. Ноль убирает строку — отдельной кнопки удаления в макете нет. */
  function setRowQuantity(key: number, quantity: number): void {
    basket.value =
      quantity > 0
        ? basket.value.map((row) => (row.key === key ? { ...row, quantity } : row))
        : basket.value.filter((row) => row.key !== key)
  }

  /*
   * Пакет появляется вместе с первым товаром и исчезает с последним. Количество
   * у него всегда одно: степпера в строке нет, менять его клиенту нечем.
   *
   * `immediate` — ради сохранённой корзины: она поднимается из localStorage
   * раньше меню, и пакет попадает в неё, как только меню приехало.
   */
  watch(
    [() => goods.value.length, packageProduct],
    ([count, product]) => {
      if (!product) return

      const inCart = basket.value.some((row) => row.productId === product.id)

      if (!count && inCart) {
        basket.value = basket.value.filter((row) => row.productId !== product.id)
        return
      }

      if (count && !inCart) {
        addToCart({ productId: product.id, quantity: 1, price: product.price, modifiers: [] })
      }
    },
    { immediate: true },
  )

  function resetCart(): void {
    basket.value = []
    /* Приборы — часть заказа, а не настройка клиента: следующий начинается заново */
    persons.value = 1
  }

  /*
   * Корзина принадлежит клиенту, а не устройству: цены, подарки и акции
   * считаются по его карте и филиалу, и в заказ она уходит от его имени.
   * Сменился клиент — набранное предыдущим убираем.
   *
   * Вход после анонимного набора не трогаем: `previous` там пуст, это тот же
   * человек, просто дошёл до регистрации с полной корзиной.
   */
  watch(
    () => userStore.userId,
    (id, previous) => {
      if (previous && id !== previous) resetCart()
    },
  )

  return {
    basket,
    cart,
    goods,
    packageId,
    persons,
    changePersons,
    cartProducts,
    totalSum,
    totalCount,
    addToCart,
    removeLastCartItem,
    setRowQuantity,
    resetCart,
  }
})
